import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  BuildingOffice2Icon,
  CalendarIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  CurrencyEuroIcon,
  DocumentArrowUpIcon,
  DocumentChartBarIcon,
  IdentificationIcon,
  LinkIcon,
  ScaleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { Cog6ToothIcon, ExclamationTriangleIcon, HomeIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { formatDateWithTime } from '../../../../common-utils/formatters';
import { BillingEntity, getBillingEntityLabel, getBillingEntityValues } from '../../../@types/BillingEntity';
import type { Company } from '../../../@types/Company';
import { setupStepOrder } from '../../../@types/Company';
import { isActivePaymentStatus, PaymentStatus } from '../../../@types/PaymentStatus';
import { paymentAdminApi } from '../../../services/paymentApi';
import type { RootState } from '../../../store/store';
import {
  downloadCompanyConfigExport,
  formatUploadAge,
  getSoftwareDisplayName,
  isCoproValidated,
} from '../../../utils/configExport';
import { CoproRnicExcelExportEmptyError, exportCoprosRnicToExcel } from '../../../utils/coproRnicExcelExport';
import { JWTHeader } from '../../../utils/JWTHeader';
import { DJ_URL, ROUTES } from '../../../utils/routes';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import KPICard from '../../common/KPICards';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import Spinner from '../../common/Spinner';
import {
  CompanyAddressContactBlock,
  CompanyLegalInfoBlock,
  CompanyLotsCards,
  CompanyServicesBlock,
} from '../components/CompanyInfoBlocks';
import { FederationLogos } from '../components/CompanyListFilters';
import SellsCard from './components/SellsCard';

const djLogo = require('../../../../img/logoDJ.svg');

interface Employee {
  id: number;
  type: number;
  user: {
    id: number;
    uuid: string;
    firstname: string;
    lastname: string;
    email: string;
    roles: string[];
    lastActivityAt: string | null;
    DJUpdatedAt: string | null;
    createdAt: string;
    updatedAt: string;
  };
}

interface KPIs {
  totalCopros: number;
  subcontractedCopros: number;
  coprosWithoutAddress: number;
  batchSizeHabitable: number;
  totalDPEs: number;
}

interface SellsData {
  [year: string]: {
    nbSales: number;
    salesAveragePrice: number;
    salesCommission: number;
  };
}

const CompanyDetails: React.FC = () => {
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [kpis, setKPIs] = useState<KPIs | null>(null);
  const [sellsData, setSellsData] = useState<SellsData | null>(null);
  const [loadingCompany, setLoadingCompany] = useState<boolean>(true);
  const [loadingEmployees, setLoadingEmployees] = useState<boolean>(true);
  const [loadingKPIs, setLoadingKPIs] = useState<boolean>(true);
  const [loadingVentes, setLoadingVentes] = useState<boolean>(true);
  const [loadingDPEDetection, setLoadingDPEDetection] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { companyId } = useParams<{ companyId: string }>();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [loadingCCI, setLoadingCCI] = useState(true);
  const [updatingPaymentStatus, setUpdatingPaymentStatus] = useState<boolean>(false);
  const [showPaymentStatusModal, setShowPaymentStatusModal] = useState<boolean>(false);
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState<boolean>(false);
  const [generatedPaymentLink, setGeneratedPaymentLink] = useState<string | null>(null);
  const [copiedPaymentLink, setCopiedPaymentLink] = useState<boolean>(false);
  const [pendingBillingEntity, setPendingBillingEntity] = useState<BillingEntity | null>(null);
  const [updatingBillingEntity, setUpdatingBillingEntity] = useState<boolean>(false);
  const [exportingCopros, setExportingCopros] = useState<boolean>(false);
  const [downloadingConfigExport, setDownloadingConfigExport] = useState<boolean>(false);

  const fetchCompanyDetails = async() => {
    if (!companyId || !jwt) {
      setError('Unable to fetch company details. Missing required information.');
      setLoadingCompany(false);
      return;
    }

    try {
      const response = await fetch(`/api/companies/${companyId}?includeCCI=true`, JWTHeader(jwt));
      if (!response.ok) {
        throw new Error(`HTTP error status: ${response.status}`);
      }
      const data: Company = await response.json();

      setCompany(data);
      setLoadingCCI(false);
    } catch (fetchError) {
      console.error('Error fetching company details :', fetchError);
      showToast('error', 'Une erreur est survenue lors du chargement des détails de l\'entreprise.');
    } finally {
      setLoadingCompany(false);
      setLoadingCCI(false);
    }
  };

  const fetchEmployees = async() => {
    if (!companyId || !jwt) {return;}

    try {
      const response = await fetch(`/api/companies/${companyId}/fonctions?limit=10000`, JWTHeader(jwt));
      if (!response.ok) {
        throw new Error(`HTTP error status: ${response.status}`);
      }
      const data = await response.json();
      setEmployees(data.results);
    } catch (fetchError) {
      console.error('Error fetching employees :', fetchError);
      showToast('error', 'Une erreur est survenue lors du chargement des employés.');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const fetchKPIs = async() => {
    if (!companyId || !jwt) {return;}

    try {
      const coprosResponse = await fetch(`/api/companies/${companyId}/copros?limit=10000&filterValidatedByUser=true`, JWTHeader(jwt));

      if (!coprosResponse.ok) {
        throw new Error(`HTTP error status: ${coprosResponse.status}`);
      }

      const coprosData = await coprosResponse.json();
      const totalCopros = coprosData.results.length;
      const coprosWithoutAddress = coprosData.results.filter((copro) => copro.addresses.length === 0).length;
      const subcontractedCopros = coprosData.results.filter((copro) => copro.company.id !== Number(companyId)
        && copro.company.subcontractors.some((subcontractor) => subcontractor.id === Number(companyId))).length;

      const statsResponse = await fetch(`/api/companies/${companyId}/stats`, JWTHeader(jwt));

      if (!statsResponse.ok) {
        throw new Error(`HTTP error status: ${statsResponse.status}`);
      }

      const statsData = await statsResponse.json();

      setKPIs({
        totalCopros,
        subcontractedCopros,
        coprosWithoutAddress,
        batchSizeHabitable: statsData.nbLogements || 0,
        totalDPEs: 0, // This will be updated when we fetch ventes data
      });
    } catch (fetchError) {
      console.error('Error fetching KPIs :', fetchError);
      showToast('error', 'Une erreur est survenue lors du chargement des KPIs.');
    } finally {
      setLoadingKPIs(false);
    }
  };

  const fetchSells = async() => {
    if (!companyId || !jwt) {return;}

    try {
      const salesResponse = await fetch(`/api/companies/${companyId}/stats/sales`, JWTHeader(jwt));

      if (!salesResponse.ok) {
        throw new Error(`HTTP error status: ${salesResponse.status}`);
      }

      const salesData = await salesResponse.json();
      setSellsData(salesData);

      // Update totalDPEs in KPIs
      const latestYear = Object.keys(salesData).sort((a, b) => Number(b) - Number(a))[0];
      setKPIs((prevKPIs) => ({
        ...prevKPIs,
        totalDPEs: salesData[latestYear].nbDpe,
      }));
    } catch (fetchError) {
      console.error('Error fetching Ventes data :', fetchError);
      showToast('error', 'Une erreur est survenue lors du chargement des données de ventes.');
    } finally {
      setLoadingVentes(false);
    }
  };

  useEffect(() => {
    if (!companyId || !jwt) {return;}
    setError(null);

    // Reset loading states to ensure UI updates
    setLoadingCompany(true);
    setLoadingEmployees(true);
    setLoadingKPIs(true);
    setLoadingVentes(true);

    fetchCompanyDetails();
    fetchEmployees();
    fetchKPIs();
    fetchSells();
  }, [companyId, jwt]);

  const handleDetectDPEs = async() => {
    if (!companyId) {return;}
    setLoadingDPEDetection(true);
    try {
      const response = await fetch(`/api/companies/${companyId}/dpes/detect-new`, {
        method: 'POST',
        ...JWTHeader(jwt),
      });
      if (!response.ok) {
        throw new Error(`HTTP error status: ${response.status}`);
      }
      showToast('success', 'Détection des DPEs lancée avec succès.');
    } catch (fetchError) {
      console.error('Error launching DPE detection :', fetchError);
      showToast('error', 'Une erreur est survenue lors du lancement de la détection des DPEs.');
    } finally {
      setLoadingDPEDetection(false);
    }
  };

  const handleSubcontractorClick = (subcontractorId: number) => {
    navigate(ROUTES.ADMIN_COMPANY_DETAILS.replace(':companyId', subcontractorId.toString()));
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleUpdatePaymentStatus = async(newStatus: PaymentStatus) => {
    if (!company || !jwt) {return;}

    try {
      setUpdatingPaymentStatus(true);
      const response = await paymentAdminApi.updatePaymentStatus(company.id, newStatus, jwt);
      
      // Update local company state with new data
      if (response.company) {
        setCompany({
          ...company,
          paymentStatus: response.payment.paymentStatus,
          proAbonoSubscriptionEnd: response.payment.subscriptionEnd,
        });
      }
      
      showToast('success', `Statut de paiement mis à jour: ${newStatus}`);
      setShowPaymentStatusModal(false);
      
      // Refresh company details to get updated data
      fetchCompanyDetails();
    } catch (err) {
      console.error('Error updating payment status:', err);
      showToast('error', 'Erreur lors de la mise à jour du statut de paiement');
    } finally {
      setUpdatingPaymentStatus(false);
    }
  };

  const currentBillingEntity = company?.billingEntity ?? BillingEntity.DECLARATIONS_JURIDIQUES;

  const handleBillingEntityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newBillingEntity = event.target.value as BillingEntity;

    if (newBillingEntity !== currentBillingEntity) {
      setPendingBillingEntity(newBillingEntity);
    }
  };

  const handleCloseBillingEntityDialog = () => {
    if (updatingBillingEntity) {return;}

    setPendingBillingEntity(null);
  };

  const handleUpdateBillingEntity = async() => {
    if (!company || !jwt || !pendingBillingEntity) {return;}

    const newBillingEntityLabel = getBillingEntityLabel(pendingBillingEntity);

    try {
      setUpdatingBillingEntity(true);
      const response = await paymentAdminApi.updateBillingEntity(company.id, pendingBillingEntity, jwt);

      // Update local company state: the ProAbono customer link is reset by the switch
      setCompany({
        ...company,
        billingEntity: response.payment?.billingEntity ?? pendingBillingEntity,
        proAbonoReference: null,
        proAbonoCreateDate: null,
      });

      // A payment link generated with the former entity is stale
      setGeneratedPaymentLink(null);
      setCopiedPaymentLink(false);

      showToast('success', `Entité de facturation mise à jour : ${newBillingEntityLabel}`);

      // Refresh company details to get updated data
      fetchCompanyDetails();
    } catch (err) {
      console.error('Error updating billing entity:', err);
      showToast('error', 'Erreur lors de la mise à jour de l\'entité de facturation');
    } finally {
      setUpdatingBillingEntity(false);
      setPendingBillingEntity(null);
    }
  };

  const handleGeneratePaymentLink = async() => {
    if (!company || !jwt) {return;}

    try {
      setGeneratingPaymentLink(true);
      setGeneratedPaymentLink(null);
      setCopiedPaymentLink(false);
      const response = await paymentAdminApi.getPaymentLink(company.id, jwt);

      if (response.paymentLink) {
        setGeneratedPaymentLink(response.paymentLink);
        showToast('success', 'Lien de paiement généré avec succès');
      } else {
        showToast('error', 'Aucun lien de paiement disponible pour cette entreprise');
      }
    } catch (err) {
      console.error('Error generating payment link:', err);
      showToast('error', 'Erreur lors de la génération du lien de paiement');
    } finally {
      setGeneratingPaymentLink(false);
    }
  };

  const handleCopyPaymentLink = () => {
    if (!generatedPaymentLink) {return;}

    navigator.clipboard.writeText(generatedPaymentLink).then(() => {
      setCopiedPaymentLink(true);
      showToast('success', 'Lien de paiement copié dans le presse-papiers');
      setTimeout(() => setCopiedPaymentLink(false), 3000);
    }).catch(() => {
      showToast('error', 'Erreur lors de la copie dans le presse-papiers');
    });
  };

  const exportCoprosToExcel = async() => {
    if (exportingCopros || !companyId || !jwt || !company) {return;}

    setExportingCopros(true);
    try {
      await exportCoprosRnicToExcel(companyId, company.siren, jwt);
      showToast('success', 'Export Excel réussi.');
    } catch (exportError) {
      if (exportError instanceof CoproRnicExcelExportEmptyError) {
        showToast('warning', exportError.message);
      } else {
        console.error('Error exporting copros to Excel :', exportError);
        showToast('error', 'Une erreur est survenue lors de l\'export Excel.');
      }
    } finally {
      setExportingCopros(false);
    }
  };

  const handleDownloadConfigExport = async() => {
    if (downloadingConfigExport || !jwt || !company) {return;}

    setDownloadingConfigExport(true);
    try {
      await downloadCompanyConfigExport(company.id, company.name, jwt);
    } catch (downloadError) {
      console.error('Error downloading config export :', downloadError);
      showToast('error', 'Une erreur est survenue lors du téléchargement du fichier d\'export.');
    } finally {
      setDownloadingConfigExport(false);
    }
  };

  const copyPointageCoproMail = () => {
    const mailContent = `Objet : MODULE CROISSANCE – PARAMÉTRAGE – ÉTAPE N°1 : Vérification de vos copropriétés gérées / Ajout des ASL, AFUL et immeubles tertiaires

Madame, Monsieur,

Bonjour,

Suite à notre visioconférence, nous vous adressons comme convenu les premiers éléments nécessaires au paramétrage du module CROISSANCE.

Vous trouverez en pièce jointe un fichier Excel comprenant la liste des copropriétés actuellement identifiées pour votre structure.

ÉTAPE N°1 : VALIDATION DES COPROPRIÉTÉS

Afin de finaliser cette première étape de paramétrage, nous vous remercions de bien vouloir :

• Vérifier que l'ensemble des copropriétés que vous gérez figure bien dans le fichier.
• Indiquer, le cas échéant, les copropriétés que vous ne gérez plus en remplaçant le « oui » en vert par « non » en rouge dans la colonne dédiée.
• Ajouter en bas du fichier toute copropriété manquante, en complétant l'ensemble des colonnes demandées.
• Ajouter également les ASL, AFUL et immeubles tertiaires éventuellement présents dans votre portefeuille.
• Renseigner les champs requis (dont l'en-tête des colonnes apparaît en jaune), notamment :
    - le numéro d'immeuble utilisé dans votre logiciel,
    - le nom du gestionnaire,
    - la prochaine date d'assemblée.

ÉTAPE SUIVANTE

• Une fois ces éléments complétés, merci de nous retourner le fichier ;
• Nous procéderons à son importation après vérification ;
• Nous vous ouvrirons ensuite l'accès aux pages de paramétrage afin que vous puissiez valider les adresses de chaque copropriété.

Nous pourrons alors finaliser la configuration graphique et lancer la mise en œuvre de votre stratégie de communication et d'action.

Nous restons bien entendu à votre disposition pour toute question relative au fichier transmis ou aux champs à compléter.`;

    navigator.clipboard.writeText(mailContent).then(() => {
      showToast('success', 'Mail de demande de pointage copié dans le presse-papiers.');
    }).catch(() => {
      showToast('error', 'Erreur lors de la copie du mail.');
    });
  };

  const employeeHeaders = [
    { title: 'NOM', class: 'w-1/4' },
    { title: 'EMAIL', class: 'w-1/4' },
    { title: 'DERNIÈRE ACTIVITÉ', class: 'w-1/4' },
  ];

  const uniqueEmployees = [...employees.reduce((map, employee) => {
      if (!map.has(employee.user.id)) {
        map.set(employee.user.id, employee);
      }
      return map;
    }, new Map<number, Employee>()).values()];

  const employeeData = uniqueEmployees
    .sort((a, b) => {
      const lastNameA = a.user.lastname.toUpperCase();
      const lastNameB = b.user.lastname.toUpperCase();

      if (lastNameA < lastNameB) {return -1;}
      if (lastNameA > lastNameB) {return 1;}

      const firstNameA = a.user.firstname.toUpperCase();
      const firstNameB = b.user.firstname.toUpperCase();

      if (firstNameA < firstNameB) {return -1;}
      if (firstNameA > firstNameB) {return 1;}

      return 0;
    })
    .map((employee) => ({
      NOM: `${employee.user.lastname.toUpperCase()} ${employee.user.firstname}`,
      EMAIL: employee.user.email,
      'DERNIÈRE ACTIVITÉ': employee.user.lastActivityAt
        ? new Date(employee.user.lastActivityAt).toLocaleString()
        : 'Jamais connecté',
    }));

  const companySetupSteps = company?.setupSteps ? Object.keys(company.setupSteps).filter((step) => company.setupSteps[step]) : [];
  const companySetupStepsCount = companySetupSteps.length;

  return (
    <div>
      <div className="rounded-lg bg-white p-4 shadow-md">
        <div className="mb-6 flex flex-col items-center justify-between md:flex-row">
          <div className="mb-4 flex w-full flex-col items-start md:mb-0 md:w-auto">
            <div className="flex w-full items-center">
              <button
                onClick={handleGoBack}
                className="mr-4 shrink-0 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <h1 className="flex items-center text-2xl font-bold text-gray-800">
                <BuildingOffice2Icon className="mr-2 h-8 w-8 shrink-0 text-rgpd-blue-dark" />
                <span className="">
                  {company && company.name ? company.name : <Skeleton width={200} />}
                </span>
              </h1>
            </div>
          </div>
          <div className="flex w-full shrink-0 items-center justify-center gap-4 md:w-auto">
            {company && DJ_URL && (
              <a
                href={`${DJ_URL}/admin/company/show/${company.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex shrink-0 items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
              >
                <img src={djLogo} alt="Déclarations Juridiques" className="h-5 w-auto" />
                Ouvrir la fiche société sur DJ
              </a>
            )}
            <FederationLogos details={company?.details} variant="header" />
          </div>
        </div>

        {error && <div className="my-8 rounded-lg bg-red-100 p-4 text-red-800">{error}</div>}

        <div className="mb-8">
          <h2 className="mb-2 flex items-center text-lg font-semibold">
            <IdentificationIcon className="mr-2 h-6 w-6 text-rgpd-blue-dark" />
            Détails de l'entreprise
          </h2>
          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
            <KPICard 
              icon={<IdentificationIcon className="h-6 w-6" />} 
              title="SIREN" 
              value={company?.siren || ''} 
              loading={loadingCompany} 
              copy 
            />
            <KPICard 
              icon={<CalendarIcon className="h-6 w-6" />} 
              title="Abonnement ProAbono" 
              value={company ? (
                isActivePaymentStatus(company.paymentStatus) && company.paymentStatus !== PaymentStatus.UNLIMITED_FREE
                  ? `Actif jusqu'au ${company.proAbonoSubscriptionEnd ? new Date(company.proAbonoSubscriptionEnd).toLocaleDateString('fr-FR') : 'Non défini'}`
                  : company.paymentStatus === PaymentStatus.UNLIMITED_FREE
                    ? 'Accès illimité gratuit'
                    : company.paymentStatus || 'Aucun abonnement'
              ) : ''
              } 
              loading={loadingCompany} 
            />
            <KPICard 
              icon={<ScaleIcon className="h-6 w-6" />} 
              title="Commission" 
              value={company ? `${company.commission}%` : ''} 
              loading={loadingCompany} 
            />
            <KPICard 
              icon={<Cog6ToothIcon className="h-6 w-6" />} 
              title="Étape de configuration validée" 
              value={`${companySetupStepsCount}/${setupStepOrder.filter(step => step !== 'vigilanceAdditionalCopro').length}`} 
              loading={loadingCompany} 
            />
          </div>

          {/* Payment Status Management Section */}
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-md mb-2 flex items-center font-semibold">
                  <CurrencyEuroIcon className="mr-2 h-5 w-5 text-blue-600" />
                  Gestion de l'abonnement
                </h3>
                <p className="flex items-center text-sm text-gray-600">
                  Statut actuel:&nbsp;
                  {loadingCompany ? (
                    <Skeleton width={100} />
                  ) : (
                    <>
                      <span className="font-semibold">{company?.paymentStatus || 'Non défini'}</span>
                      {company?.paymentStatus === PaymentStatus.UNLIMITED_FREE && (
                        <span className="ml-2 rounded bg-green-100 px-2 py-1 text-xs text-green-800">
                          Accès illimité gratuit actif
                        </span>
                      )}
                    </>
                  )}
                </p>
                <div className="mt-2 flex items-center text-sm text-gray-600">
                  <label htmlFor="company-billing-entity">Facturé par :&nbsp;</label>
                  {loadingCompany ? (
                    <Skeleton width={160} />
                  ) : (
                    <>
                      <select
                        id="company-billing-entity"
                        value={currentBillingEntity}
                        onChange={handleBillingEntityChange}
                        disabled={!company || updatingBillingEntity || generatingPaymentLink}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-semibold focus:border-rgpd-blue focus:ring-rgpd-blue disabled:opacity-50"
                      >
                        {getBillingEntityValues().map((billingEntity) => (
                          <option key={billingEntity} value={billingEntity}>
                            {getBillingEntityLabel(billingEntity)}
                          </option>
                        ))}
                      </select>
                      {updatingBillingEntity && <Spinner size="sm" className="ml-2" />}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleGeneratePaymentLink}
                  className="flex items-center rounded bg-blue-500 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-600 disabled:opacity-50"
                  disabled={generatingPaymentLink || updatingBillingEntity || loadingCompany}
                >
                  {generatingPaymentLink ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <LinkIcon className="mr-2 h-4 w-4" />
                  )}
                  {generatingPaymentLink ? 'Génération...' : 'Générer un lien de paiement'}
                </button>
                <button
                  onClick={() => setShowPaymentStatusModal(!showPaymentStatusModal)}
                  className="rounded bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                  disabled={updatingPaymentStatus || loadingCompany}
                >
                  {showPaymentStatusModal ? 'Masquer' : 'Modifier le statut'}
                </button>
              </div>
            </div>

            {/* Generated Payment Link */}
            {generatedPaymentLink && (
              <div className="mt-3 rounded-lg border border-blue-300 bg-white p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="mb-1 text-xs font-medium text-gray-500">Lien de paiement ProAbono</p>
                    <p className="truncate font-mono text-sm text-gray-700">{generatedPaymentLink}</p>
                  </div>
                  <button
                    onClick={handleCopyPaymentLink}
                    className={`flex items-center rounded px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                      copiedPaymentLink
                        ? 'bg-green-500 text-white'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    {copiedPaymentLink ? (
                      <>
                        <CheckIcon className="mr-2 h-4 w-4" />
                        Copié !
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="mr-2 h-4 w-4" />
                        Copier le lien
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Payment Status Selection Modal */}
            {showPaymentStatusModal && (
              <div className="mt-4 border-t border-blue-300 pt-4">
                <h4 className="mb-3 font-semibold">Choisir un nouveau statut :</h4>
                
                {/* Special status */}
                <div className="mb-4">
                  <p className="mb-2 text-xs font-medium text-gray-500 uppercase">Statut spécial</p>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                    <button
                      onClick={() => handleUpdatePaymentStatus(PaymentStatus.UNLIMITED_FREE)}
                      disabled={updatingPaymentStatus}
                      className="rounded-lg border-2 border-green-500 bg-green-50 p-3 text-left transition-colors hover:bg-green-100 disabled:opacity-50"
                    >
                      <div className="font-semibold text-green-700">Unlimited Free</div>
                      <div className="text-xs text-green-600">Accès illimité gratuit</div>
                    </button>
                  </div>
                </div>

                {/* Standard offers with onboarding fees */}
                <div className="mb-4">
                  <p className="mb-2 text-xs font-medium text-gray-500 uppercase">Offres standard (avec frais de mise en service)</p>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                    <button
                      onClick={() => handleUpdatePaymentStatus(PaymentStatus.INACTIVE_UNDER_1000)}
                      disabled={updatingPaymentStatus}
                      className="rounded-lg border-2 border-gray-500 bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100 disabled:opacity-50"
                    >
                      <div className="font-semibold text-gray-700">Inactive (&lt; 1000)</div>
                      <div className="text-xs text-gray-600">89€/mois (frais: 490€)</div>
                    </button>
                    <button
                      onClick={() => handleUpdatePaymentStatus(PaymentStatus.INACTIVE_1000_TO_2000)}
                      disabled={updatingPaymentStatus}
                      className="rounded-lg border-2 border-gray-500 bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100 disabled:opacity-50"
                    >
                      <div className="font-semibold text-gray-700">Inactive (1000-2000)</div>
                      <div className="text-xs text-gray-600">149€/mois (frais: 980€)</div>
                    </button>
                    <button
                      onClick={() => handleUpdatePaymentStatus(PaymentStatus.INACTIVE_OVER_2000)}
                      disabled={updatingPaymentStatus}
                      className="rounded-lg border-2 border-gray-500 bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100 disabled:opacity-50"
                    >
                      <div className="font-semibold text-gray-700">Inactive (&gt; 2000)</div>
                      <div className="text-xs text-gray-600">249€/mois (frais: 1980€)</div>
                    </button>
                  </div>
                </div>

                {/* Promotional offers without onboarding fees */}
                <div className="mb-4">
                  <p className="mb-2 text-xs font-medium text-orange-600 uppercase">Offres promotionnelles (sans frais de mise en service)</p>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                    <button
                      onClick={() => handleUpdatePaymentStatus(PaymentStatus.INACTIVE_UNDER_1000_NO_FEE)}
                      disabled={updatingPaymentStatus}
                      className="rounded-lg border-2 border-orange-500 bg-orange-50 p-3 text-left transition-colors hover:bg-orange-100 disabled:opacity-50"
                    >
                      <div className="font-semibold text-orange-700">Inactive (&lt; 1000) Sans frais</div>
                      <div className="text-xs text-orange-600">89€/mois - frais offerts</div>
                    </button>
                    <button
                      onClick={() => handleUpdatePaymentStatus(PaymentStatus.INACTIVE_1000_TO_2000_NO_FEE)}
                      disabled={updatingPaymentStatus}
                      className="rounded-lg border-2 border-orange-500 bg-orange-50 p-3 text-left transition-colors hover:bg-orange-100 disabled:opacity-50"
                    >
                      <div className="font-semibold text-orange-700">Inactive (1000-2000) Sans frais</div>
                      <div className="text-xs text-orange-600">149€/mois - frais offerts</div>
                    </button>
                    <button
                      onClick={() => handleUpdatePaymentStatus(PaymentStatus.INACTIVE_OVER_2000_NO_FEE)}
                      disabled={updatingPaymentStatus}
                      className="rounded-lg border-2 border-orange-500 bg-orange-50 p-3 text-left transition-colors hover:bg-orange-100 disabled:opacity-50"
                    >
                      <div className="font-semibold text-orange-700">Inactive (&gt; 2000) Sans frais</div>
                      <div className="text-xs text-orange-600">249€/mois - frais offerts</div>
                    </button>
                  </div>
                </div>

                {/* 50% Promotional offers on onboarding fees */}
                <div className="mb-4">
                  <p className="mb-2 text-xs font-medium text-purple-600 uppercase">Offres promotionnelles (-50% sur frais de mise en service)</p>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                    <button
                      onClick={() => handleUpdatePaymentStatus(PaymentStatus.INACTIVE_UNDER_1000_50_PROMO)}
                      disabled={updatingPaymentStatus}
                      className="rounded-lg border-2 border-purple-500 bg-purple-50 p-3 text-left transition-colors hover:bg-purple-100 disabled:opacity-50"
                    >
                      <div className="font-semibold text-purple-700">Inactive (&lt; 1000) -50%</div>
                      <div className="text-xs text-purple-600">89€/mois - 50% sur frais (245€)</div>
                    </button>
                    <button
                      onClick={() => handleUpdatePaymentStatus(PaymentStatus.INACTIVE_1000_TO_2000_50_PROMO)}
                      disabled={updatingPaymentStatus}
                      className="rounded-lg border-2 border-purple-500 bg-purple-50 p-3 text-left transition-colors hover:bg-purple-100 disabled:opacity-50"
                    >
                      <div className="font-semibold text-purple-700">Inactive (1000-2000) -50%</div>
                      <div className="text-xs text-purple-600">149€/mois - 50% sur frais (490€)</div>
                    </button>
                    <button
                      onClick={() => handleUpdatePaymentStatus(PaymentStatus.INACTIVE_OVER_2000_50_PROMO)}
                      disabled={updatingPaymentStatus}
                      className="rounded-lg border-2 border-purple-500 bg-purple-50 p-3 text-left transition-colors hover:bg-purple-100 disabled:opacity-50"
                    >
                      <div className="font-semibold text-purple-700">Inactive (&gt; 2000) -50%</div>
                      <div className="text-xs text-purple-600">249€/mois - 50% sur frais (990€)</div>
                    </button>
                  </div>
                </div>

                {updatingPaymentStatus && (
                  <div className="mt-3 flex items-center text-blue-600">
                    <Spinner size="sm" className="mr-2" />
                    <span>Mise à jour en cours...</span>
                  </div>
                )}
              </div>
            )}

            {/* Billing Entity Change Confirmation */}
            <ConfirmationDialog
              open={pendingBillingEntity !== null}
              title="Changer l'entité de facturation"
              confirmLabel="Oui, changer l'entité"
              cancelLabel="Annuler"
              onConfirm={handleUpdateBillingEntity}
              onClose={handleCloseBillingEntityDialog}
              loading={updatingBillingEntity}
              description={(
                <div className="max-w-xl space-y-4">
                  <p className="text-gray-700">
                    L'entreprise <span className="font-semibold">{company?.name}</span> sera désormais facturée
                    par <span className="font-semibold">{getBillingEntityLabel(pendingBillingEntity)}</span> au
                    lieu de <span className="font-semibold">{getBillingEntityLabel(currentBillingEntity)}</span>.
                  </p>

                  <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <ExclamationTriangleIcon className="mt-0.5 h-6 w-6 shrink-0 text-amber-500" />
                    <div className="space-y-2">
                      <p className="font-medium text-amber-800">
                        L'abonnement existant chez {getBillingEntityLabel(currentBillingEntity)} doit être
                        résilié manuellement dans ProAbono.
                      </p>
                      <p className="text-sm text-amber-700">
                        Ce changement ne résilie aucun abonnement : pensez à l'annuler côté ancienne entité
                        pour éviter une double facturation.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 text-gray-700">
                    <p className="font-medium">Conséquences :</p>
                    <ul className="ml-2 list-inside list-disc space-y-1 text-sm text-gray-600">
                      <li>
                        Le lien client ProAbono de l'entreprise est réinitialisé : le client sera recréé ou
                        rattaché dans le compte ProAbono de {getBillingEntityLabel(pendingBillingEntity)} lors
                        de la prochaine utilisation.
                      </li>
                      <li>
                        Le statut de paiement actuel ({company?.paymentStatus || 'Non défini'}) est conservé :
                        l'accès de l'entreprise n'est pas modifié.
                      </li>
                      <li>Un lien de paiement déjà généré n'est plus valable.</li>
                    </ul>
                  </div>
                </div>
              )}
            />
          </div>

          {/* Config export file uploaded by the company (setup step "coproValidation") */}
          <div className="mb-4 rounded-lg bg-gray-50 p-4">
            <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
              <div>
                <h3 className="text-md mb-2 flex items-center font-semibold">
                  <DocumentArrowUpIcon className="mr-2 h-5 w-5 text-rgpd-blue-dark" />
                  Fichier d'export de configuration
                </h3>
                {loadingCompany ? (
                  <>
                    <Skeleton width={280} className="mb-1" />
                    <Skeleton width={200} />
                  </>
                ) : company?.configExportFileName ? (
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>
                      {company.configExportUploadedAt ? (
                        <>
                          Déposé le{' '}
                          <span className="font-semibold text-gray-800">{formatDateWithTime(company.configExportUploadedAt)}</span>
                          {' '}({formatUploadAge(company.configExportUploadedAt)})
                        </>
                      ) : 'Date de dépôt inconnue'}
                    </p>
                    <p className="flex flex-wrap items-center gap-2">
                      Logiciel :
                      <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs text-blue-800">
                        {getSoftwareDisplayName(company.selectedSoftware)}
                      </span>
                      {isCoproValidated(company) ? (
                        <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs text-green-800">Copros validées</span>
                      ) : (
                        <span className="rounded-full bg-orange-100 px-2.5 py-1 text-xs text-orange-800">Import en attente</span>
                      )}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">Aucun fichier d'export déposé par l'entreprise.</p>
                )}
              </div>
              {company?.configExportFileName && (
                <button
                  onClick={handleDownloadConfigExport}
                  disabled={downloadingConfigExport}
                  className="flex shrink-0 items-center justify-center rounded bg-rgpd-blue-dark px-4 py-2 text-sm text-white transition-colors hover:bg-rgpd-blue disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {downloadingConfigExport ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
                  )}
                  Télécharger le fichier
                </button>
              )}
            </div>
          </div>

          <CompanyAddressContactBlock details={company?.details} loading={loadingCompany} />
          <CompanyLegalInfoBlock details={company?.details} loading={loadingCCI} />
          <CompanyServicesBlock details={company?.details} loading={loadingCompany} />
          <CompanyLotsCards details={company?.details} loading={loadingCompany} />
        </div>

        {/* KPIs */}
        <div className="mb-8">
          <div className="mb-4 flex flex-col justify-between md:flex-row md:items-center">
            <h2 className="mb-2 flex items-center text-lg font-semibold md:mb-0">
              <ClipboardDocumentListIcon className="mr-2 h-6 w-6 text-rgpd-blue-dark" />
              KPI des copropriétés
            </h2>
            <div className="flex w-full flex-col gap-2 md:w-auto md:items-end">
              <button
                onClick={exportCoprosToExcel}
                disabled={exportingCopros || loadingCompany}
                className={`flex w-full items-center justify-center rounded bg-rgpd-green px-4 py-2 text-white transition-colors hover:bg-rgpd-green-dark md:w-auto ${exportingCopros || loadingCompany ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                {exportingCopros ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Export en cours...
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="mr-2 h-5 w-5" />
                    Exporter les copropriétés (Excel)
                  </>
                )}
              </button>
              <button
                onClick={copyPointageCoproMail}
                className="flex w-full items-center justify-center rounded bg-rgpd-blue px-4 py-2 text-white transition-colors hover:bg-rgpd-blue-dark md:w-auto"
              >
                <ClipboardDocumentIcon className="mr-2 h-5 w-5" />
                Copier le mail de demande de pointage des copros
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            <KPICard
              icon={<BuildingOffice2Icon className="h-6 w-6" />}
              title="Total de copropriétés"
              value={kpis && kpis.totalCopros}
              subtitles={[
                { label: 'qui lui sont sous-traitées', value: kpis && kpis.subcontractedCopros },
              ]}
              loading={loadingKPIs}
            />
            <KPICard
              icon={<HomeIcon className="h-6 w-6" />}
              title="Nombre de lots habitable"
              value={kpis && kpis.batchSizeHabitable}
              loading={loadingKPIs}
            />
            <KPICard
              icon={<DocumentChartBarIcon className="h-6 w-6" />}
              title="DPEs détectés"
              value={kpis && kpis.totalDPEs}
              loading={loadingKPIs || loadingVentes}
            />
          </div>
        </div>

        {/* Ventes */}
        <div className="mb-8">
          <h2 className="mb-4 flex items-center text-lg font-semibold">
            <CurrencyEuroIcon className="mr-2 h-6 w-6 text-rgpd-blue-dark" />
            Ventes
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
            {loadingVentes ? (
              <>
                <SellsCard year="202X (en cours)" data={{}} loading />
                <SellsCard year="202X" data={{}} loading />
                <SellsCard year="202X" data={{}} loading />
                <SellsCard year="202X" data={{}} loading />
              </>
            ) : (
              Object.entries(sellsData || {})
                .sort(([a], [b]) => Number(b) - Number(a))
                .slice(0, 4)
                .map(([year, data]) => (
                  <SellsCard
                    key={year}
                    year={year === new Date().getFullYear().toString() ? `${year} (en cours)` : year}
                    data={data}
                  />
                ))
            )}
          </div>
        </div>

        {/* Subcontractors */}
        {company && company.subcontractors && company.subcontractors.length > 0 && (
          <div className="mb-8">
            <h2 className="mb-4 flex items-center text-lg font-semibold">
              <BuildingOffice2Icon className="mr-2 h-6 w-6 text-rgpd-blue-dark" />
              Sous-traitants (
              {company.subcontractors.length}
              )
            </h2>
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
              {company.subcontractors.map((subcontractor) => (
                <li
                  key={subcontractor.id}
                  className="cursor-pointer rounded-lg bg-gray-100 p-4 transition-colors hover:bg-gray-200"
                  onClick={() => handleSubcontractorClick(subcontractor.id)}
                >
                  <p className="font-semibold">{subcontractor.name}</p>
                  <p className="text-sm text-gray-600">
                    SIREN :{' '}
                    {subcontractor.siren}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Subcontractor for */}
        {company && company.subcontractorFor && company.subcontractorFor.length > 0 && (
          <div className="mb-8">
            <h2 className="mb-4 flex items-center text-lg font-semibold">
              <BuildingOffice2Icon className="mr-2 h-6 w-6 text-rgpd-blue-dark" />
              Mandants (
              {company.subcontractorFor.length}
              )
            </h2>
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
              {company.subcontractorFor.map((subcontractorFor) => (
                <li
                  key={subcontractorFor.id}
                  className="cursor-pointer rounded-lg bg-gray-100 p-4 transition-colors hover:bg-gray-200"
                  onClick={() => handleSubcontractorClick(subcontractorFor.id)}
                >
                  <p className="font-semibold">{subcontractorFor.name}</p>
                  <p className="text-sm text-gray-600">
                    SIREN :{' '}
                    {subcontractorFor.siren}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mb-0 flex flex-col-reverse justify-between md:mb-6 md:flex-row md:items-center">
          <h2 className="flex items-center text-lg font-semibold">
            <UserGroupIcon className="mr-2 h-6 w-6 text-rgpd-blue-dark" />
            Employés (
            {loadingEmployees ? <Skeleton width={20} /> : uniqueEmployees.length}
            )
          </h2>
          <button
            onClick={handleDetectDPEs}
            disabled={loadingDPEDetection}
            className={`mb-4 flex w-full items-center justify-center rounded bg-rgpd-green px-4 py-2 text-white transition-colors hover:bg-rgpd-green-dark md:mb-0 md:w-auto ${loadingDPEDetection ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            {loadingDPEDetection ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Détection en cours...
              </>
            ) : (
              <>
                <DocumentChartBarIcon className="mr-2 h-5 w-5" />
                Lancer la détection des DPEs
              </>
            )}
          </button>
        </div>

        {loadingEmployees ? (
          <Skeleton count={5} height={40} className="mb-2" />
        ) : (
          <MUITable
            data={employeeData}
            headers={employeeHeaders}
            tableClass="w-full"
            isSearchable
            sortableColumns={['NOM', 'EMAIL', 'DERNIÈRE ACTIVITÉ']}
          />
        )}
      </div>
    </div>
  );
};

export default CompanyDetails;
